/** Ported 1:1 from portal-cliente-ole.source.html (OV + SCREEN_STATES). */

import { isSafePaymentUrl } from './api/paymentHandoff';

export const BOARD_STATE_KEYS = [
  /**
   * La solicitud acaba de enviarse (la firma ES el envío) y ASA todavía no
   * decidió: corre asíncrono desde `auto_review`. No es «en evaluación» —
   * eso es una etapa del negocio, con un humano revisando. Son segundos de
   * máquina, así que el tablero espera y no ofrece acciones.
   */
  'processing',
  'initial',
  'review',
  'uwreq',
  'counter',
  'approved',
  'denied',
  'active',
  'cancelled',
] as const;

export type BoardStateKey = (typeof BOARD_STATE_KEYS)[number];

export type StepKind = 'done' | 'cur' | 'warn' | 'ok' | 'bad' | '';

export type OverviewChrome = {
  pill: string;
  text: string;
  steps: readonly [StepKind, StepKind, StepKind, StepKind];
};

export const OV: Record<BoardStateKey, OverviewChrome> = {
  processing: { pill: 'pill-info', text: 'En proceso', steps: ['done', '', '', ''] },
  initial: { pill: 'pill-warning', text: 'Faltan documentos', steps: ['done', 'cur', '', ''] },
  review: { pill: 'pill-info', text: 'En evaluación', steps: ['done', 'done', 'cur', ''] },
  uwreq: { pill: 'pill-warning', text: 'Acción requerida', steps: ['done', 'done', 'warn', ''] },
  counter: { pill: 'pill-warning', text: 'Nueva oferta', steps: ['done', 'done', 'done', 'warn'] },
  approved: { pill: 'pill-info', text: 'Falta el pago', steps: ['done', 'done', 'done', 'cur'] },
  denied: { pill: 'pill-danger', text: 'No aprobada', steps: ['done', 'done', 'done', 'bad'] },
  active: { pill: 'pill-success', text: 'Vigente', steps: ['done', 'done', 'done', 'ok'] },
  /* El paso marcado depende de dónde se cortó: acá se muestra el corte en
     Requisitos, que es el caso más común (vencen los 30 días). */
  cancelled: { pill: 'pill-neutral', text: 'Cancelada', steps: ['done', 'bad', '', ''] },
};

/**
 * 03E mientras se espera el aviso del primer pago (OUW-67): la píldora no le
 * dice «Falta el pago» a quien quizá ya pagó. Mismos pasos que 03E.
 */
export const PAYMENT_CONFIRMING_CHROME: OverviewChrome = { ...OV.approved, text: 'Confirmando el pago' };

export const BOARD_STATE_ALIASES: Record<string, BoardStateKey> = {
  processing: 'processing',
  '03A': 'initial',
  '03B': 'review',
  '03C': 'uwreq',
  '03D': 'counter',
  '03E': 'approved',
  '03F': 'denied',
  '03G': 'active',
  '03H': 'cancelled',
  initial: 'initial',
  review: 'review',
  uwreq: 'uwreq',
  counter: 'counter',
  approved: 'approved',
  denied: 'denied',
  active: 'active',
  cancelled: 'cancelled',
};

export function resolveBoardState(param: string | null | undefined): BoardStateKey {
  if (!param) return 'initial';
  return BOARD_STATE_ALIASES[param] ?? 'initial';
}

export type ApiVariantInput = {
  category: string;
  status: string;
  displayStatus?: string;
  hasPendingRequirements?: boolean;
  /**
   * Overlay the machine does not promote: after PAY-5 the request stays
   * `confirmed` and this becomes `first_payment_confirmed` (03G).
   */
  issuanceStage?: string;
};

/**
 * Estados en los que la solicitud está siendo procesada por la máquina y
 * todavía no hay nada que mostrarle al cliente:
 *
 * - `auto_review`: ASA corriendo (efecto asíncrono del envío).
 * - `pending_data_entry`: la firma ya ocurrió pero el webhook que avanza la
 *   máquina todavía no llegó — son dos avisos distintos de Logalty y no hay
 *   orden garantizado. Dura lo que tarda el otro webhook.
 */
const PROCESSING_STATUSES = new Set(['auto_review', 'pending_data_entry']);

export function isProcessingStatus(status: string): boolean {
  return PROCESSING_STATUSES.has((status || '').toLowerCase());
}

/**
 * OUW-325: `approved` nunca es un estado de reposo. Un efecto del outbox
 * (`derive_conformity` en api-client) la saca enseguida hacia la oferta
 * (`offer_issued`), la conformidad (`awaiting_confirmation`) o `confirmed`.
 * Con ASA la aprobación llega segundos después de firmar: el cliente cae en
 * el tablero justo en ese hueco, ve «Confirmar» deshabilitado, y sin volver a
 * preguntar el botón no se habilitaba nunca. Mientras dure, el tablero sondea.
 */
export function isAwaitingConformityStatus(status: string): boolean {
  return (status || '').toLowerCase() === 'approved';
}

/**
 * Maps GET /intake/applications category+status (+ issuance overlay) → board.
 * Raw `status` never reaches the UI; it only breaks ties inside a category.
 * `displayStatus` is accepted for the call signature and ignored (opacity).
 * `issuanceStage === first_payment_confirmed` is 03G (PAY-5 self-loop).
 */
export function variantFromApi(input: ApiVariantInput): BoardStateKey {
  const category = (input.category || '').toLowerCase();
  const status = (input.status || '').toLowerCase();
  void input.displayStatus;
  void input.hasPendingRequirements;

  if (isProcessingStatus(status)) return 'processing';

  // PAY-5 leaves the machine on `confirmed` (self-loop). 03G is the issuance
  // overlay, and only of a confirmed request: a stale stage on a cancelled or
  // rejected one must never read as «Tu póliza está activa».
  if (status === 'confirmed' && (input.issuanceStage || '').toLowerCase() === 'first_payment_confirmed') {
    return 'active';
  }

  // Documentos pendientes: el status es la señal, no la categoría.
  // `pending_initial_requirements` es `completing` (caía en 03B).
  // `pending_manual_requirements` es `waiting_client` (caía en 03A).
  if (status === 'pending_initial_requirements') return 'initial';
  if (status === 'pending_manual_requirements') return 'uwreq';

  if (category === 'decided') {
    if (status === 'approved' || status === 'confirmed') return 'approved';
    if (status === 'rejected') return 'denied';
    return 'cancelled';
  }

  if (category === 'waiting_client') {
    if (status === 'offer_issued') return 'counter';
    if (status === 'awaiting_confirmation') return 'approved';
    return 'review';
  }

  return 'review';
}

/**
 * Sub-fase de la variante `approved` (03E, MX): dentro de esa pantalla el
 * cliente puede estar esperando confirmar, esperando pagar, o sin ninguna
 * acción pendiente. Vive junto a `variantFromApi` para que el vocabulario de
 * estados no se repita comparando `application.status` crudo en el panel
 * (hallazgo del review de OUW-70: `ApprovedPanel` re-derivaba lo mismo con un
 * `.toLowerCase()` que ningún otro sitio usa).
 *
 * OUW-108: `none` ya no es «cero botón». Es el próximo paso esperado, todavía
 * no accionable (Confirmar antes de `awaiting_confirmation`, Pagar sin
 * `paymentUrl`). `approvedCtaFromApi` es esa decisión; `approvedPhaseFromApi`
 * sigue siendo el enabled/none que ya consume el tablero.
 */
export type ApprovedPhase = 'confirm' | 'pay' | 'none';
export type ApprovedCtaKind = 'confirm' | 'pay';
export type ApprovedCta = { kind: ApprovedCtaKind; enabled: boolean };

export function approvedCtaFromApi(input: { status: string; paymentUrl?: unknown }): ApprovedCta {
  const status = (input.status || '').toLowerCase();
  if (status === 'confirmed') {
    return { kind: 'pay', enabled: isSafePaymentUrl(input.paymentUrl) };
  }
  return { kind: 'confirm', enabled: status === 'awaiting_confirmation' };
}

export function approvedPhaseFromApi(input: { status: string; paymentUrl?: unknown }): ApprovedPhase {
  const cta = approvedCtaFromApi(input);
  return cta.enabled ? cta.kind : 'none';
}

export type LandingView = 'form' | 'error' | 'expired' | 'locked' | 'ok';
export type HelpView = 'default' | 'sent';

export type ScreenState = { id: string; label: string };

export const SCREEN_STATES: {
  landing: ScreenState[];
  help: ScreenState[];
  overview: ScreenState[];
  flow: ScreenState[];
} = {
  landing: [
    { id: 'form', label: 'Por defecto' },
    { id: 'error', label: 'Nombre no coincide' },
    { id: 'expired', label: 'Enlace vencido' },
    { id: 'locked', label: 'Bloqueado' },
    { id: 'ok', label: 'Verificado' },
  ],
  help: [
    { id: 'default', label: 'Por defecto' },
    { id: 'sent', label: 'Enlace enviado' },
  ],
  overview: [],
  flow: [],
};

export function resolveLandingView(param: string | null | undefined): LandingView {
  const allowed: LandingView[] = ['form', 'error', 'expired', 'locked', 'ok'];
  return allowed.includes(param as LandingView) ? (param as LandingView) : 'form';
}

export function resolveHelpView(param: string | null | undefined): HelpView {
  return param === 'sent' ? 'sent' : 'default';
}

export const BOARD_TAB_META: { alias: string; state: BoardStateKey; label: string }[] = [
  { alias: '03A', state: 'initial', label: 'Requisitos iniciales' },
  { alias: '03B', state: 'review', label: 'En evaluación' },
  { alias: '03C', state: 'uwreq', label: 'Requisitos del UW' },
  { alias: '03D', state: 'counter', label: 'Nueva oferta' },
  { alias: '03E', state: 'approved', label: 'Aprobada' },
  { alias: '03F', state: 'denied', label: 'Negada' },
  { alias: '03G', state: 'active', label: 'Vigente' },
  { alias: '03H', state: 'cancelled', label: 'Cancelada' },
];
